import { useEffect, type RefObject } from 'react';

/**
 * Scroll arrastrando para punteros tipo "mouse". El táctil infrarrojo USB puede reportarse
 * como mouse según el driver, y un mouse no desplaza contenedores con overflow. Con
 * pointerType "touch" se deja el scroll nativo. Incluye inercia y evita el click accidental
 * al terminar un arrastre.
 */
export function useDragScroll(ref: RefObject<HTMLElement | null>, axis: 'x' | 'y') {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let id: number | null = null;
    let start = 0;
    let startScroll = 0;
    let last = 0;
    let lastT = 0;
    let v = 0;
    let dragging = false;
    let raf = 0;

    const pos = (e: PointerEvent) => (axis === 'x' ? e.clientX : e.clientY);
    const scale = () => {
      const r = el.getBoundingClientRect();
      return axis === 'x' ? r.width / el.clientWidth || 1 : r.height / el.clientHeight || 1;
    };
    const setScroll = (val: number) => {
      if (axis === 'x') el.scrollLeft = val;
      else el.scrollTop = val;
    };
    const getScroll = () => (axis === 'x' ? el.scrollLeft : el.scrollTop);

    const down = (e: PointerEvent) => {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      cancelAnimationFrame(raf);
      id = e.pointerId;
      start = last = pos(e);
      lastT = performance.now();
      startScroll = getScroll();
      v = 0;
      dragging = false;
    };
    const move = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      const p = pos(e);
      const k = scale();
      if (!dragging && Math.abs(p - start) > 10) {
        dragging = true;
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* puntero ya liberado */
        }
      }
      if (!dragging) return;
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      v = ((last - p) / k / dt) * 0.8 + v * 0.2;
      last = p;
      lastT = now;
      setScroll(startScroll - (p - start) / k);
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      id = null;
      if (!dragging) return;
      // el click que sigue a un arrastre no debe abrir la tarjeta
      const kill = (ev: Event) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      el.addEventListener('click', kill, { capture: true, once: true });
      setTimeout(() => el.removeEventListener('click', kill, { capture: true }), 50);
      let vel = v * 16;
      const step = () => {
        if (Math.abs(vel) < 0.5) return;
        setScroll(getScroll() + vel);
        vel *= 0.94;
        raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };

    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
  }, [ref, axis]);
}
